import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Button from 'components/common/Button'
import TextInput from 'components/common/TextInput'
import c from './AuthForm.module.scss'

interface AuthFormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  email: string
  password?: string
  submitText: string
  isSubmitting: boolean
  onEmailChange: (newEmail: string) => void
  onPasswordChange?: (newPassword: string) => void
  onForgotPassword?: () => void
}

function AuthForm({
  className,
  email,
  password = undefined,
  submitText,
  isSubmitting,
  onEmailChange,
  onPasswordChange = undefined,
  onForgotPassword = undefined,
  ...props
}: AuthFormProps) {
  const { t } = useTranslation()

  const classNames = useMemo(() => {
    let result = c.form
    if (className) result += ` ${className}`
    return result
  }, [className])

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onEmailChange(e.target.value)
    },
    [onEmailChange],
  )

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onPasswordChange) onPasswordChange(e.target.value)
    },
    [onPasswordChange],
  )

  return (
    <form className={classNames} {...props}>
      <TextInput
        className={c.formInput}
        required
        type="email"
        placeholder={t('general.formPlaceholders.email')}
        value={email}
        onChange={handleEmailChange}
      />
      {password != null && (
        <TextInput
          className={c.formInput}
          required
          type="password"
          placeholder={t('general.formPlaceholders.password')}
          value={password}
          onChange={handlePasswordChange}
        />
      )}
      {onForgotPassword && (
        <Button
          className={c.formForgotPassword}
          variant="text"
          onClick={onForgotPassword}
        >
          {t('modals.logIn.forgotPassword')}
        </Button>
      )}
      <Button className={c.formSubmit} type="submit" disabled={isSubmitting}>
        {submitText}
      </Button>
    </form>
  )
}

export default AuthForm
